/**
 * Project Analytics content (live, data-driven) — MOSYMPD-451.
 *
 * Rendered by ProjectAnalytics after "View Analytics"; the parent fetches the
 * analytics for the current selection and passes it in as `data`:
 *   type="inProcess"      → data = { context, batches[], plots[] }
 *                           Plot per full-coverage purity operation + a
 *                           Batch Comparison Matrix (readings per batch).
 *   type="productRelease" → data = { campaignId, subtitle, stages[] }
 *                           Campaign Release Map (stage swimlanes, per-batch
 *                           P/A cards) + Detailed Release Results table.
 *
 * All values, stages, ordering and colour bands come from the API — no bundled
 * data and no stage/test assumptions here. Colours are keyed by the band the
 * API returns (see analyticsConfig / FSD §5).
 */
import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { RELEASE_BANDS, RELEASE_LEGEND_KEYS, bandStyleOf, ASSAY_CELL_STYLE } from './analyticsConfig';
import { LineChart, Legend } from './Charts';

/**
 * Styles follow the Project Development vocabulary used across the module
 * (see BatchGenealogy/Styles.js, ProjectTabs/GenericTableStyles.js): Roboto at
 * rem sizes, #000/#333 text, #E0E0E0 borders, #F9F9FC fills, 4px radii. Only
 * the release-band colours are local, and those come from analyticsConfig
 * because they carry meaning.
 */
const useStyles = makeStyles((theme) => ({
  // The app renders no <CssBaseline /> and sets no body font, so bare
  // <span>/<div> children would inherit the browser's serif default (MUI only
  // injects Roboto into Typography/TableCell). Declaring the font on the roots
  // below makes every plain element — and the Recharts SVG labels — inherit it.
  root: { fontFamily: theme.typography.fontFamily },
  note: { background: '#F9F9FC', border: '1px solid #E0E0E0', color: '#333', fontSize: '0.875rem', lineHeight: 1.5, padding: '10px 16px', borderRadius: '4px', margin: theme.spacing(0.5, 0, 2) },
  // Section headings ("Campaign Release Map …", "Detailed release results") in
  // the brand navy, matching the results title in ProjectAnalytics/Styles.js.
  panelTitle: { fontWeight: 600, fontSize: '16px', color: '#173072', margin: theme.spacing(0.5, 0, 0.25) },
  panelSub: { fontSize: '0.875rem', color: '#333', margin: theme.spacing(0, 0, 1.5), maxWidth: 900 },
  card: { border: '1px solid #E0E0E0', borderRadius: '4px', padding: theme.spacing(2), background: '#fff', marginBottom: theme.spacing(2) },
  sectionHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), cursor: 'pointer' },
  sectionSummary: { marginLeft: 'auto', fontSize: '0.875rem', color: '#333' },
  coverageBadge: { flexShrink: 0, fontSize: '0.75rem', fontWeight: 600, color: '#000', background: '#F9F9FC', border: '1px solid #E0E0E0', borderRadius: '4px', padding: '4px 12px', whiteSpace: 'nowrap' },
  // Same navy as panelTitle — the In-Process counterpart of those headings.
  subHeading: { fontWeight: 600, fontSize: '16px', color: '#173072', margin: theme.spacing(2.5, 0, 1) },
  headerCell: { fontWeight: 'bold', color: '#000', background: '#F9F9FC', fontSize: '0.875rem', padding: '10px', whiteSpace: 'nowrap' },
  // Batch-number columns in "Recorded values per batch": same header fill, but
  // normal weight so the row of batch IDs doesn't read as bolder than its data.
  batchHeaderCell: { fontWeight: 400, color: '#000', background: '#F9F9FC', fontSize: '0.875rem', padding: '10px', whiteSpace: 'nowrap' },
  cell: { fontSize: '0.875rem', color: '#333', padding: '10px', borderBottom: '1px solid #E0E0E0' },
  // Single container for the caption + every stage row. The border lives here
  // (not on the individual rows), so the group reads as one table-like block.
  releaseGroup: {
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: theme.spacing(1.5),
  },
  // Caption as a header strip INSIDE the box — margin: 0 so it doesn't push a
  // gap above the first row.
  qcOrderLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#333',
    letterSpacing: '0.04em',
    margin: 0,
    padding: '8px 16px',
    background: '#F9F9FC',
    borderBottom: '1px solid #E0E0E0',
  },
  // A row in the group, not a card: no border, no radius, no bottom margin —
  // just a divider between siblings, suppressed on the last one.
  swimlane: {
    borderBottom: '1px solid #E0E0E0',
    '&:last-child': { borderBottom: 'none' },
  },
  laneHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), padding: '10px 16px', background: '#fff', cursor: 'pointer' },
  laneName: { fontWeight: 600, color: '#000', fontSize: '0.875rem' },
  // Stage mode pill ("Purity only" / "Purity + Assay" / "Assay only") — same
  // navy tint as the test-type chip in the results header (ProjectAnalytics
  // `chipType`), so the two read as the same kind of label.
  modePill: { fontSize: '0.75rem', fontWeight: 600, color: '#173072', background: '#E8EDF7', border: '1px solid #C7D3EA', borderRadius: '4px', padding: '2px 10px' },
  laneCount: { fontSize: '0.875rem', color: '#333' },
  laneSummary: { marginLeft: 'auto', fontSize: '0.875rem', color: '#333' },
  // borderTop replaces the divider that used to sit under laneHead, so the rule
  // appears only when a stage is actually expanded.
  cardsWrap: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5), padding: theme.spacing(2), borderTop: '1px solid #E0E0E0', background: '#FCFCFD' },
  batchCard: { width: 150, border: '1px solid #E0E0E0', borderRadius: '4px', padding: '8px 10px', background: '#fff' },
  batchId: { fontSize: '0.875rem', fontWeight: 600, color: '#000', marginBottom: theme.spacing(0.75) },
  paRow: { display: 'flex', gap: theme.spacing(0.75) },
  paPill: { flex: 1, fontSize: '0.75rem', fontWeight: 500, textAlign: 'center', borderRadius: '4px', padding: '3px 0' },
  cellPill: { display: 'inline-block', minWidth: 54, fontSize: '0.75rem', fontWeight: 500, textAlign: 'center', borderRadius: '4px', padding: '2px 8px' },
  loading: { padding: theme.spacing(5), textAlign: 'center', color: '#333', fontSize: '0.875rem', fontFamily: theme.typography.fontFamily },
  // Status is a pill, like the purity/assay cells beside it.
  statusPill: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '4px',
    padding: '2px 10px',
    whiteSpace: 'nowrap',
  },
  statusPass: { background: '#E6F4EA', color: '#1E7E34' },
  statusFail: { background: '#FDECEA', color: '#C62828' },
  // Anything neither pass- nor fail-like. Deliberately neutral: see statusTone.
  statusNeutral: { background: '#F1F3F5', color: '#000' },

  // ── "Detailed release results" table ───────────────────────────────────────
  // `fixed` layout + the <colgroup> below give the six columns a stable, evenly
  // balanced distribution. With the default `auto` layout the two long headers
  // ("Chromatographic Purity", "Assay (% w/w)") inflated their own columns while
  // the short ones collapsed, and mixing align="right" on those two against
  // left-aligned neighbours stranded each value at the far edge of its column.
  // Everything is centre-aligned now — one rule for the whole table, matching
  // the PD tables in BatchGenealogy.
  // minWidth keeps the columns readable on narrow screens — TableContainer
  // scrolls horizontally rather than crushing them.
  // minWidth raised to 900 for the full column titles — below that the two long
  // UPLC headers wrap too aggressively, so TableContainer scrolls instead.
  detailTable: { tableLayout: 'fixed', width: '100%', minWidth: 900 },
  // The table scrolls on both axes inside its own box instead of stretching the
  // analytics panel: horizontally for the 900px minWidth above, vertically
  // against the viewport so the header row (sticky, below) stays put while
  // scrolling through every batch.
  detailTableContainer: {
    border: '1px solid #E0E0E0',
    marginTop: 12,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 400px)',
  },
  detailHeaderCell: {
    fontWeight: 'bold',
    color: '#000',
    background: '#F9F9FC',
    // Sticky set here so the opaque #F9F9FC wins over MUI's stickyHeader
    // theme background and body rows don't show through while scrolling.
    position: 'sticky',
    top: 0,
    zIndex: 2,
    borderBottom: '1px solid #E0E0E0',
    fontSize: '0.875rem',
    padding: '10px 8px',
    // Left, header and body alike — every column reads from the same edge, so
    // each value sits directly under its own heading.
    textAlign: 'left',
    verticalAlign: 'middle',
    // Must wrap: a fixed column can't widen to fit a long header.
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  detailCell: {
    fontSize: '0.875rem',
    color: '#000',
    padding: '10px 8px',
    borderBottom: '1px solid #E0E0E0',
    // Left, header and body alike — every column reads from the same edge, so
    // each value sits directly under its own heading.
    textAlign: 'left',
    verticalAlign: 'middle',
    wordBreak: 'break-word',
  },

  // ── Per-checkpoint Table A / Table B (In-Process FSD, "Table Display") ─────
  // Two tables sit directly beneath each checkpoint's line chart. The FSD is
  // explicit that they carry NO descriptive subtitle or explanatory note
  // between or below them — "they are self-contained" — so there is
  // deliberately no caption element in this block.
  checkpointTables: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  auditTableContainer: {
    border: '1px solid #E0E0E0',
    overflowX: 'auto',
  },
  // First column carries the row label ("Reaction-monitoring purity (%)",
  // "MBR Number / Op. No"); the rest are one column per batch.
  auditRowLabelCell: {
    fontSize: '0.875rem',
    color: '#000',
    fontWeight: 600,
    padding: '10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
    background: '#FCFCFD',
  },
  auditValueCell: {
    fontSize: '0.875rem',
    color: '#333',
    padding: '10px',
    borderBottom: '1px solid #E0E0E0',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },

  // ── per-checkpoint audit trail (In-Process FSD) ───────────────────────────
  // COLLAPSED by default, deliberately: the FSD requires the two tables above
  // to be "self-contained", with no descriptive subtitle or note between or
  // below them — so the default view is exactly Table A + Table B, and this
  // disclosure only opens when the user asks for it.
  auditToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: theme.spacing(1),
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#173072',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  auditPanel: {
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    background: '#F9F9FC',
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1),
  },
  auditGroupLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#333',
    margin: theme.spacing(0, 0, 0.5),
  },
  auditGroupLabelSpaced: { marginTop: theme.spacing(1.5) },
  auditList: { margin: 0, paddingLeft: theme.spacing(2.5) },
  auditListItem: { fontSize: '0.75rem', color: '#333', lineHeight: 1.6 },
  auditEmpty: { fontSize: '0.75rem', color: '#6B6B6B', fontStyle: 'italic' },
}));

const fmt1 = (v) => (v == null ? '—' : Number(v).toFixed(1));

// Shown as a hover hint on a metric the middleware flagged as a source data
// issue. FSD (§Filters, Business Rules): "where a batch carries more than one
// qualifying Purity result, or more than one dried-basis Assay result, the
// condition is raised as a source data issue at ingestion and the metric is
// shown unbanded until it is resolved." Without this the cell just looks grey
// for no visible reason.
const DATA_ISSUE_HINT =
  'More than one qualifying result is recorded for this batch — shown unbanded until the source data is resolved.';

/**
 * Table A + Table B for one checkpoint (In-Process FSD, "Table Display").
 *
 *   Table A — latest recorded value: one row, the plotted v(b, checkpoint) for
 *             every batch (the same values the line chart shows).
 *   Table B — MBR / operation / test-number audit: two rows —
 *             "MBR Number / Op. No" (Method Number masked to N/A, e.g.
 *             "N/A - 16a") and "Version / Test #" (plain version + observed
 *             test slot, e.g. "00 - Test 4").
 *
 * Both are built entirely from what the middleware sends per plot (`tableA` /
 * `tableB`, keyed by batch number) — this component derives nothing. The FSD
 * requires no subtitle or note between or below the two tables, so none is
 * rendered here; the plot's own header above the chart is separate.
 *
 * Falls back to the plotted values when a payload predates tableA (so an older
 * middleware still shows Table A rather than nothing), and simply omits Table B
 * when the audit rows are absent — the audit detail cannot be reconstructed
 * frontend-side.
 */
const CheckpointTables = ({ plot, batches, classes }) => {
  // Collapsed by default — see the auditToggle style note: the FSD wants the
  // two tables to be the whole of the default view.
  const [auditOpen, setAuditOpen] = useState(false);

  const tableARow =
    (plot.tableA && plot.tableA.byBatch) ||
    batches.reduce((acc, b, i) => {
      acc[b] = plot.values ? plot.values[i] : null;
      return acc;
    }, {});
  const tableALabel = (plot.tableA && plot.tableA.rowLabel) || `${plot.test} (${plot.uom || '%'})`;

  const auditRows = plot.tableB
    ? [plot.tableB.mbrOpNoRow, plot.tableB.versionTestRow].filter(
        (r) => r && r.byBatch
      )
    : [];

  // Alias wordings and superseded readings the middleware resolved for this
  // checkpoint. Shown only when there is something to show, so a clean
  // checkpoint carries no extra control at all.
  const aliases = Array.isArray(plot.aliases) ? plot.aliases : [];
  const superseded = (plot.audit && Array.isArray(plot.audit.superseded))
    ? plot.audit.superseded
    : [];
  const hasAudit = aliases.length > 0 || superseded.length > 0;

  return (
    <div className={classes.checkpointTables}>
      {/* ── Table A — latest recorded value per batch ── */}
      <TableContainer component={Paper} elevation={0} className={classes.auditTableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.batchHeaderCell}>Checkpoint</TableCell>
              {batches.map((b) => (
                <TableCell key={b} className={classes.batchHeaderCell} align="right">{b}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={classes.auditRowLabelCell}>{tableALabel}</TableCell>
              {batches.map((b) => (
                <TableCell key={b} className={classes.auditValueCell}>{fmt1(tableARow[b])}</TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Table B — MBR / operation / test-number audit per batch ── */}
      {auditRows.length > 0 && (
        <TableContainer component={Paper} elevation={0} className={classes.auditTableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.batchHeaderCell}>Audit field</TableCell>
                {batches.map((b) => (
                  <TableCell key={b} className={classes.batchHeaderCell} align="right">{b}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {auditRows.map((row) => (
                <TableRow key={row.rowLabel}>
                  <TableCell className={classes.auditRowLabelCell}>{row.rowLabel}</TableCell>
                  {batches.map((b) => (
                    <TableCell key={b} className={classes.auditValueCell}>
                      {row.byBatch[b] || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Audit trail: alias wordings + superseded readings ──
          The FSD keeps both rather than discarding them — a checkpoint's other
          observed wordings are "retained as an alias, visible in the audit view
          rather than discarded", and a reading superseded by a later QC Receipt
          Date is "not carried into the analytics, but still visible in the audit
          trail". The middleware already returns both per plot (`aliases`,
          `audit.superseded`); without this they were computed and dropped.
          No audit SCREEN is specified anywhere in the FSD, so this is the
          minimum that satisfies "visible" — collapsed, in place, under the
          checkpoint it belongs to. */}
      {hasAudit && (
        <>
          <button
            type="button"
            className={classes.auditToggle}
            onClick={() => setAuditOpen((o) => !o)}
            aria-expanded={auditOpen}
          >
            {auditOpen ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}
            {`Audit trail (${aliases.length} alias wording(s) · ${superseded.length} superseded reading(s))`}
          </button>
          {auditOpen && (
            <div className={classes.auditPanel}>
              <p className={classes.auditGroupLabel}>Other wordings observed for this checkpoint</p>
              {aliases.length ? (
                <ul className={classes.auditList}>
                  {aliases.map((a) => (
                    <li className={classes.auditListItem} key={a}>{a}</li>
                  ))}
                </ul>
              ) : (
                <div className={classes.auditEmpty}>
                  None — every row used the canonical wording.
                </div>
              )}

              <p className={`${classes.auditGroupLabel} ${classes.auditGroupLabelSpaced}`}>
                Superseded readings (an earlier reading replaced by a later QC Receipt Date)
              </p>
              {superseded.length ? (
                <ul className={classes.auditList}>
                  {superseded.map((s, i) => (
                    <li className={classes.auditListItem} key={`${s.batch}-${s.op}-${i}`}>
                      {[
                        `Batch ${s.batch}`,
                        s.value != null ? `${fmt1(s.value)}` : null,
                        s.op ? `operation ${s.op}` : null,
                        s.qcReceipt ? `QC receipt ${s.qcReceipt}` : null,
                      ].filter(Boolean).join(' · ')}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={classes.auditEmpty}>
                  None — every batch carried a single reading for this checkpoint.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Status -> pill tone. Fail-like is checked FIRST and unrecognised values fall
 * through to neutral, never to pass: the previous `/fail/i.test(s) ? red : green`
 * test painted anything that wasn't literally "fail" green, so a "Rejected" or
 * "Out of Spec" batch would have shown in pass-green.
 */
const statusToneClass = (status, classes) => {
  const s = String(status || '').trim().toLowerCase();
  if (/fail|reject|out of spec|oos|below|not met|invalid/.test(s)) return classes.statusFail;
  if (/pass|complete|approved|released|within|conform/.test(s)) return classes.statusPass;
  return classes.statusNeutral;
};

const PdAnalyticsGraphs = ({ type = 'inProcess', data = null, loading = false, selectedStage = '' }) => {
  const classes = useStyles();
  const stages = (data && data.stages) || [];
  const [openStages, setOpenStages] = useState([]);
  const [mapOpen, setMapOpen] = useState(true);
  const [detailOpen, setDetailOpen] = useState(true);
  // Every stage swimlane is visible; only the selected stage is expanded by
  // default (the API also echoes it as data.selectedStage). If the selection
  // isn't among the returned stages, fall back to expanding the first stage so
  // the map is never fully collapsed on open.
  useEffect(() => {
    const names = stages.map((s) => String(s.name));
    const target = String((data && data.selectedStage) || selectedStage || '');
    const initial = names.includes(target) ? target : names[0];
    setOpenStages(initial != null ? [initial] : []);
  }, [data, selectedStage]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleStage = (name) =>
    setOpenStages((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  if (loading) return <div className={classes.loading}>Loading analytics…</div>;

  // ─────────────────────────── Product Release Tests ───────────────────────────
  if (type === 'productRelease') {
    if (!stages.length) return <div className={classes.loading}>No product release tests for this selection.</div>;
    const totalBatches = stages.reduce((n, s) => n + s.batches.length, 0);
    const stageHasP = (s) => /purity/i.test(s.mode);
    const stageHasA = (s) => /assay/i.test(s.mode);

    // The Detailed Release Results table is served ready-made by the
    // middleware (`detailedTable`), already ordered by stage then QC receipt
    // and — crucially — already carrying `notInPanel` per metric, resolved from
    // the STAGE's derived test profile. Consuming it keeps this table and the
    // Campaign Release Map on one source of truth ("The table and the Campaign
    // Release Map are two views of the same batch set and always agree on
    // values, bands, and counts").
    //
    // The fallback below re-derives the same rows for a payload that predates
    // `detailedTable`. It sets notInPanel from the stage profile too — NOT from
    // "value is null", which is what previously made a stage that runs a metric
    // but has no recorded value for one batch read as "not in panel".
    const detailRows = Array.isArray(data.detailedTable) && data.detailedTable.length
      ? data.detailedTable
      : stages.flatMap((stage) => {
          const hasP = stageHasP(stage);
          const hasA = stageHasA(stage);
          return stage.batches.map((bt) => ({
            stage: stage.name,
            batch: bt.id,
            qcReceipt: bt.qcReceipt || '',
            purity: {
              value: bt.p,
              limit: bt.purityLimit,
              band: hasP ? bt.pBand : 'not_in_panel',
              notInPanel: !hasP,
              dataIssue: !!bt.pDataIssue,
            },
            assay: {
              value: bt.a,
              specText: bt.assaySpecText || '',
              notInPanel: !hasA,
              dataIssue: !!bt.aDataIssue,
            },
            status: bt.status,
          }));
        });

    return (
      <div className={classes.root}>
        {/* Campaign Release Map — stage swimlanes */}
        <Paper elevation={0} className={classes.card}>
          <div className={classes.sectionHead} onClick={() => setMapOpen((o) => !o)}>
            {mapOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            <Typography className={classes.panelTitle} style={{ margin: 0 }}>{`Campaign Release Map — ${data.campaignId}`}</Typography>
            <span className={classes.sectionSummary}>{`${stages.length} stages · ${totalBatches} batches`}</span>
          </div>
          {mapOpen && (
            <>
              {data.subtitle && <Typography className={classes.panelSub} style={{ marginTop: 8 }}>{data.subtitle}</Typography>}
              <Legend items={RELEASE_LEGEND_KEYS.map((k) => ({
                label: RELEASE_BANDS[k].label,
                color: RELEASE_BANDS[k].color,
              }))} />
              {/* One box holds the caption and every stage row: the caption is a
                  header strip inside the border, and the rows are separated by
                  dividers rather than being individually bordered cards. */}
              <div className={classes.releaseGroup}>
              <Typography className={classes.qcOrderLabel}>RELEASE BATCHES · QC-RECEIPT ORDER</Typography>
              {stages.map((stage) => {
                const open = openStages.includes(stage.name);
                const hasP = stageHasP(stage);
                const hasA = stageHasA(stage);
                return (
                  <div key={stage.name} className={classes.swimlane}>
                    <div className={classes.laneHead} onClick={() => toggleStage(stage.name)}>
                      {open ? <ExpandMoreIcon fontSize="small" style={{ color: '#333' }} /> : <ChevronRightIcon fontSize="small" style={{ color: '#333' }} />}
                      <span className={classes.laneName}>{`Stage ${stage.name}`}</span>
                      <span className={classes.modePill}>{stage.mode}</span>
                      <span className={classes.laneCount}>{`${stage.batches.length} batches`}</span>
                      <span className={classes.laneSummary}>{stage.summary}</span>
                    </div>
                    {open && (
                      <div className={classes.cardsWrap}>
                        {stage.batches.map((bt) => (
                          <div key={bt.id} className={classes.batchCard}>
                            <div className={classes.batchId}>{bt.id}</div>
                            <div className={classes.paRow}>
                              {hasP && (
                                <span className={classes.paPill} style={bandStyleOf(bt.pBand)}>
                                  {bt.p != null ? `P ${fmt1(bt.p)}` : 'P —'}
                                </span>
                              )}
                              {hasA && (
                                <span className={classes.paPill} style={ASSAY_CELL_STYLE}>
                                  {bt.a != null ? `A ${fmt1(bt.a)}` : 'A —'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </>
          )}
        </Paper>

        {/* Detailed release results */}
        <Paper elevation={0} className={classes.card}>
          <div className={classes.sectionHead} onClick={() => setDetailOpen((o) => !o)}>
            {detailOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            <Typography className={classes.panelTitle} style={{ margin: 0 }}>Detailed release results</Typography>
            <span className={classes.sectionSummary}>{`${totalBatches} batches · Purity & Assay`}</span>
          </div>
          {detailOpen && (
            <TableContainer component={Paper} elevation={0} className={classes.detailTableContainer}>
              <Table size="small" stickyHeader className={classes.detailTable}>
                {/* Stage is a single letter so it takes the least; the two UPLC
                    columns carry much longer headings plus their pills, so they
                    take the largest share. Sums to 100%. */}
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '13%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    {/* detailHeaderCell (centred, wraps) must be used here — NOT the
                        shared headerCell, which is left-aligned and nowrap. The body
                        cells below are centred, so any mismatch here shows up as
                        headers sitting off to one side of their values. No align=
                        overrides: alignment comes from the class alone. */}
                    <TableCell className={classes.detailHeaderCell}>Stage</TableCell>
                    <TableCell className={classes.detailHeaderCell}>Batch</TableCell>
                    <TableCell className={classes.detailHeaderCell}>QC Receipt</TableCell>
                    <TableCell className={classes.detailHeaderCell}>Chromatographic purity by UPLC (% area)</TableCell>
                    <TableCell className={classes.detailHeaderCell}>Assay by UPLC (% w/w) (on dried basis)</TableCell>
                    <TableCell className={classes.detailHeaderCell}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRows.map((row) => (
                    <TableRow key={`${row.stage}-${row.batch}`}>
                      {/* Letter only — the column header already says "Stage". */}
                      <TableCell className={classes.detailCell}>{row.stage}</TableCell>
                      {/* Batch ID at normal weight, same as every other value. */}
                      <TableCell className={classes.detailCell}>{row.batch}</TableCell>
                      <TableCell className={classes.detailCell}>{row.qcReceipt || '—'}</TableCell>
                      {/* FSD: "'Not in panel' is shown wherever a stage does not
                          run a metric; it is NOT the same as a missing or failed
                          result." So notInPanel — a property of the STAGE's test
                          profile, resolved server-side — is what selects that
                          text; a stage that runs the metric but has no recorded
                          value for this batch shows a plain em dash instead. */}
                      <TableCell className={classes.detailCell}>
                        {row.purity.notInPanel ? (
                          <span className={classes.cellPill} style={bandStyleOf('not_in_panel')}>
                            — (not in panel)
                          </span>
                        ) : row.purity.value != null ? (
                          <span
                            className={classes.cellPill}
                            style={bandStyleOf(row.purity.band)}
                            title={row.purity.dataIssue ? DATA_ISSUE_HINT : undefined}
                          >
                            {`${fmt1(row.purity.value)}${row.purity.limit != null ? ` (NLT ${row.purity.limit})` : ''}`}
                          </span>
                        ) : (<span style={{ color: '#666' }}>—</span>)}
                      </TableCell>
                      <TableCell className={classes.detailCell}>
                        {row.assay.notInPanel ? (
                          <span className={classes.cellPill} style={ASSAY_CELL_STYLE}>
                            — (not in panel)
                          </span>
                        ) : row.assay.value != null ? (
                          <span
                            className={classes.cellPill}
                            style={ASSAY_CELL_STYLE}
                            title={row.assay.dataIssue ? DATA_ISSUE_HINT : undefined}
                          >
                            {`${fmt1(row.assay.value)} % w/w${row.assay.specText ? ` (${row.assay.specText})` : ''}`}
                          </span>
                        ) : (<span style={{ color: '#666' }}>—</span>)}
                      </TableCell>
                      <TableCell className={classes.detailCell}>
                        {row.status ? (
                          <span className={`${classes.statusPill} ${statusToneClass(row.status, classes)}`}>
                            {row.status}
                          </span>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </div>
    );
  }

  // ─────────────────────────── In-Process Purity ───────────────────────────
  const batches = (data && data.batches) || [];
  const plots = (data && data.plots) || [];
  if (!plots.length) return <div className={classes.loading}>No full-coverage purity test for this stage.</div>;

  return (
    <div className={classes.root}>
      {/* Coverage is per CHECKPOINT, not per operation number. The FSD's Plot
          Eligibility Rule is explicit: a checkpoint is plotted when it is
          recorded for every batch "regardless of the operation number(s) or
          test-slot number(s) under which it appears" — an MBR version bump or a
          retest that moves the operation number does not break coverage. The
          earlier wording here ("plotted for an operation only when that
          operation is recorded for every batch") described the pre-checkpoint
          behaviour and now contradicts what the middleware actually does. */}
      <Typography className={classes.note}>
        Descriptive view — every point is a recorded result from the source in-process test file. No thresholds, alerts, control limits or statistical flags are added. A purity checkpoint is plotted only when it is recorded for every batch of the stage (coverage N/N), regardless of the operation or test-slot numbers it was recorded under; a checkpoint missing from any batch is not plotted.
      </Typography>

      {plots.map((p) => (
        <Paper key={p.key} elevation={0} className={classes.card}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <Typography className={classes.panelTitle}>{p.title}</Typography>
              <Typography className={classes.panelSub}>
                {`${p.operations && p.operations !== '—' ? `Operation:  ${p.operations} · ` : ''}recorded in ${p.coverage.covered} of ${p.coverage.total} batches (coverage ${p.coverage.covered}/${p.coverage.total})`}
                {p.basis ? ` · basis "${p.basis}"` : ''}
              </Typography>
            </div>
            <span className={classes.coverageBadge}>{`Coverage ${p.coverage.covered}/${p.coverage.total}`}</span>
          </div>
          <Legend items={[{ label: `${p.test} (${p.uom})`, color: p.color || '#7c6df2' }]} />
          <LineChart categories={batches} values={p.values} color={p.color || '#7c6df2'} />
          {/* FSD "Table Display": the two tables sit beneath THIS checkpoint's
              plot, one pair per checkpoint — not as a single shared table. */}
          <CheckpointTables plot={p} batches={batches} classes={classes} />
        </Paper>
      ))}

      {/* FSD "Table Display" (Table A): "The full per-batch values are also
          given in the Batch Comparison Matrix (Table P1)" — this is that
          matrix, one row per checkpoint across every batch. */}
      <Typography className={classes.subHeading}>Batch Comparison Matrix</Typography>
      <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #E0E0E0' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/* Every header at normal weight; only the first column's VALUE is
                  bold (see the row below), so the test name is the one emphasis.
                  "TEST" (was "PURITY TEST") — the value is the test name
                  (methodName / sampleDescription). "OPERATION / NUMBER" (was
                  "OPERATION(S)") — "operationDescription / operationNumber" for
                  that test (both parts when present), from the API. */}
              <TableCell className={classes.batchHeaderCell}>TEST</TableCell>
              <TableCell className={classes.batchHeaderCell}>UOM</TableCell>
              <TableCell className={classes.batchHeaderCell}>SPEC / BASIS</TableCell>
              <TableCell className={classes.batchHeaderCell}>OPERATION / NUMBER</TableCell>
              {/* Batch columns at normal weight — batch numbers are data, not
                  emphasis, so they match the values beneath them. */}
              {batches.map((b) => (<TableCell key={b} className={classes.batchHeaderCell} align="right">{b}</TableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {plots.map((p) => (
              <TableRow key={p.key}>
                {/* Only bold value in the table — the test name. */}
                <TableCell className={classes.cell} style={{ fontWeight: 600 }}>{p.test}</TableCell>
                <TableCell className={classes.cell}>{p.uom}</TableCell>
                {/* Spec/basis and operation are plain text, not links — no blue. */}
                <TableCell className={classes.cell}>{p.basis || '—'}</TableCell>
                <TableCell className={classes.cell}>{p.operations}</TableCell>
                {batches.map((b, i) => {
                  const readings = (p.readings && p.readings[i]) || (p.values[i] != null ? [p.values[i]] : []);
                  return (
                    <TableCell key={`${p.key}-${b}`} className={classes.cell} align="right">
                      {readings.length ? readings.map((v) => Number(v).toFixed(1)).join(', ') : '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PdAnalyticsGraphs;
